import {
    Box,
    Button,
    Icon,
    Paper,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {
    MouseEvent as ReactMouseEvent,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { AccountSubscriptionType } from "../../api/psc";
import TutorialWrapper from "../../features/tutorialModule/TutorialWrapper";
import useLocalStorageState from "../../hooks/useLocalStorageState";
import useResize from "../../hooks/useResize";
import RenderRelativeToAnchor from "./RenderRelativeToAnchor";

export const ResizeHandle = (props: {
    projectColumnOpen: boolean;
    handleResizeStart: (event: ReactMouseEvent<HTMLDivElement>) => void;
    setIsHovering: (isHovering: boolean) => void;
}) => {
    const handleMouseEnter = () => {
        props.setIsHovering(true);
    };
    const handleMouseLeave = () => {
        props.setIsHovering(false);
    };

    return (
        <Box
            onMouseDown={props.handleResizeStart}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            sx={{
                position: "absolute",
                right: "-12px",
                top: "50%",
                transform: "translate(-50%, -50%)",
                width: "12px",
                height: "100px",
                borderRadius: "999px",
                cursor: props.projectColumnOpen ? "col-resize" : "default",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "primary.main",
                border: (theme) => `1px solid ${theme.palette.divider}`,
                boxShadow: (theme) =>
                    theme.shadows[props.projectColumnOpen ? 2 : 0],
                display: props.projectColumnOpen ? "flex" : "none",
                transition: "background-color 150ms ease",
                zIndex: 1,
            }}
        >
            <Box
                sx={{
                    width: "2px",
                    height: "60%",
                    bgcolor: "white",
                }}
            />
        </Box>
    );
};

export const SubscriptionTypeAccountChangeMessage = [
    {
        id: AccountSubscriptionType.ULTIMATE_PLUS,
        title: "کاربر عزیز پرشین سازه",
        message: "اشتراک شما از «تابان» به «تابان پلاس» ارتقا پیدا کرد.",
        text: "با هدف پوشش درخواست‌ها و سلایق مختلف، اشتراک‌های پرشین سازه از ۳ مدل به ۴ مدل تغییر کرده. براین‌اساس، اشتراک شما به طور رایگان یک مرحله ارتقا پیدا کرد. شما هم‌چنان به تمامی پروژه‌ها بدون محدودیت دسترسی دارید و پروژه‌های خیلی بزرگ فقط برای این اشتراک (تابان پلاس) دردسترس هستند. این ارتقا بدون هیچ‌گونه هزینه‌ی اضافه، به‌عنوان هدیه‌ای از طرف پرشین سازه در اختیار شما قرار گرفته است.",
    },
    {
        id: AccountSubscriptionType.ULTIMATE,
        title: "کاربر عزیز پرشین سازه",
        message: "اشتراک شما از «رویان» به «تابان» ارتقا پیدا کرد.",
        text: "با هدف پوشش درخواست‌ها و سلایق مختلف، اشتراک‌های پرشین سازه از ۳ مدل به ۴ مدل تغییر کرده. براین‌اساس، اشتراک شما به طور رایگان یک مرحله ارتقا پیدا کرد و اکنون به پروژه‌های تا ۷۰۰ متر زمین نیز دسترسی دارید. این ارتقا بدون هیچ‌گونه هزینه‌ی اضافه، به‌عنوان هدیه‌ای از طرف پرشین سازه در اختیار شما قرار گرفته است.",
    },
    {
        id: AccountSubscriptionType.ENHANCED,
        title: "کاربر عزیز پرشین سازه",
        message: "اشتراک شما از «بنیان» به «رویان» ارتقا پیدا کرد.",
        text: "با هدف پوشش درخواست‌ها و سلایق مختلف، اشتراک‌های پرشین سازه از ۳ مدل به ۴ مدل تغییر کرده. براین‌اساس، اشتراک شما به طور رایگان یک مرحله ارتقا پیدا کرد و اکنون به پروژه‌های تا ۵۰۰ متر زمین نیز دسترسی دارید. این ارتقا بدون هیچ‌گونه هزینه‌ی اضافه، به‌عنوان هدیه‌ای از طرف پرشین سازه در اختیار شما قرار گرفته است.",
    },
];

export default function PageWithFilter(props: {
    filter: JSX.Element;
    children?: JSX.Element | JSX.Element[];
    closableChildren?: boolean;
    closableProjects?: boolean;
    projects?: JSX.Element;
    triggerMapResize?: () => void;
}) {
    const theme = useTheme();
    const isMediumOrUp = useMediaQuery(theme.breakpoints.up("md"));
    const projectColumnRef = useRef<HTMLDivElement>(null);
    const filterRef = useRef<HTMLDivElement>(null);
    const [isHoveringHandle, setIsHoveringHandle] = useState(false);

    const [projectColumnOpen, setProjectColumnOpen] =
        useLocalStorageState<boolean>("projectColumnOpen", true);

    const [filterOpen, setFilterOpen] = useLocalStorageState<boolean>(
        "filterOpen",
        true
    );

    useEffect(() => {
        if (!props.closableChildren) {
            setFilterOpen(true);
        }
        if (!props.closableProjects) {
            setProjectColumnOpen(true);
        }
    }, [props.closableChildren, props.closableProjects]);

    const [projectColumnWidth, setProjectColumnWidth] =
        useLocalStorageState<number>("projectColumnWidth", 450);

    const { size, resizeStart, setReference } = useResize({
        initialSize: {
            width: projectColumnWidth,
        },
        boundries: {
            MIN_COLUMN_WIDTH: 450,
            MAX_COLUMN_WIDTH: window.innerWidth - 350,
            MIN_HEIGHT: 0,
            MAX_HEIGHT: window.innerHeight,
        },
        onResize: (width: number) => {
            if (props.triggerMapResize) {
                props.triggerMapResize();
            }
        },
    });

    useEffect(() => {
        setProjectColumnWidth(size.width);
    }, [size.width]);

    useEffect(() => {
        setReference(projectColumnRef.current);
    }, [projectColumnRef.current]);

    const filterColumnStyles = useMemo(() => {
        if (!filterOpen) {
            return {
                width: 0,
                minWidth: 0,
            };
        }

        return {
            width: "350px",
            minWidth: "350px",
        };
    }, [filterOpen]);

    const projectColumnStyles = useMemo(() => {
        if (!projectColumnOpen) {
            return {
                width: 0,
                minWidth: 0,
            };
        }

        return {
            width: `${size.width}px`,
            minWidth: `${size.width}px`,
        };
    }, [size.width, projectColumnOpen]);

    const handleToggleProjectColumn = () => {
        setProjectColumnOpen((prev) => !prev);
        if (props.triggerMapResize) {
            props.triggerMapResize();
        }
    };

    const handleToggleFilterColumn = () => {
        setFilterOpen((prev) => !prev);
        if (props.triggerMapResize) {
            props.triggerMapResize();
        }
    };

    return (
        <Box id="page-with-filter" sx={{ height: "100%" }}>
            <Stack direction="row" sx={{ height: "100%" }}>
                {isMediumOrUp ? (
                    <>
                        <Box
                            ref={filterRef}
                            sx={{
                                ...filterColumnStyles,
                                overflow: "hidden",
                            }}
                        >
                            <TutorialWrapper
                                steps={["desktop-signup-4", "mobile-signup-4"]}
                                position="left"
                                renderChildren={(_, control: any) => (
                                    <Paper
                                        sx={{
                                            width: "350px",
                                            overflowY: "auto",
                                            overflowX: "hidden",
                                            height: "100%",
                                            borderRadius: 0,
                                            position: "relative",
                                        }}
                                        {...control}
                                    >
                                        {props.filter}
                                    </Paper>
                                )}
                            ></TutorialWrapper>
                        </Box>

                        {props.projects && (
                            <Box
                                ref={projectColumnRef}
                                sx={{
                                    ...projectColumnStyles,
                                    height: "100%",
                                    overflow: "visible",
                                    position: "relative",
                                    borderRight: (theme) =>
                                        isHoveringHandle
                                            ? `1px solid ${theme.palette.primary.main}`
                                            : `1px solid transparent`,
                                }}
                            >
                                <ResizeHandle
                                    setIsHovering={setIsHoveringHandle}
                                    projectColumnOpen={projectColumnOpen}
                                    handleResizeStart={resizeStart}
                                />
                                <Paper
                                    sx={{
                                        width: "100%",
                                        borderRadius: 0,
                                        height: "100%",
                                        overflowY: "hidden",
                                    }}
                                >
                                    {props.projects}
                                </Paper>
                            </Box>
                        )}

                        <Box
                            sx={{
                                flex: 1,
                                height: "100%",
                                overflow: "auto",
                            }}
                        >
                            {props.children}
                        </Box>
                    </>
                ) : (
                    <>
                        <Box
                            sx={{
                                flex: 1,
                                height: "100%",
                                overflow: "auto",
                            }}
                        >
                            {props.children}
                        </Box>
                        {props.projects && (
                            <Paper
                                sx={{
                                    borderRadius: 0,
                                }}
                            >
                                {props.projects}
                            </Paper>
                        )}
                    </>
                )}
            </Stack>

            {isMediumOrUp && props.closableChildren && (
                <RenderRelativeToAnchor
                    anchor={filterRef}
                    position="top left"
                    gap={{ top: 16, left: 16 }}
                >
                    <Button
                        color="purple"
                        sx={{
                            borderRadius: 100,
                        }}
                        variant="contained"
                        onClick={handleToggleFilterColumn}
                    >
                        فیلترها
                        {filterOpen ? (
                            <Icon>keyboard_double_arrow_right</Icon>
                        ) : (
                            <Icon>keyboard_double_arrow_left</Icon>
                        )}
                    </Button>
                </RenderRelativeToAnchor>
            )}

            {isMediumOrUp && props.closableProjects && (
                <RenderRelativeToAnchor
                    anchor={projectColumnRef}
                    position="bottom left"
                    gap={{ bottom: 40, right: 16 }}
                >
                    <Button
                        color="purple"
                        sx={{
                            borderRadius: 100,
                        }}
                        variant="contained"
                        onClick={handleToggleProjectColumn}
                    >
                        پروژه‌ها
                        {projectColumnOpen ? (
                            <Icon>keyboard_double_arrow_right</Icon>
                        ) : (
                            <Icon>keyboard_double_arrow_left</Icon>
                        )}
                    </Button>
                </RenderRelativeToAnchor>
            )}
        </Box>
    );
}
